// src/hooks/useAppNavigation.ts

import { DomainType } from '@/Shared';
import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';


/**
 * useAppNavigation hook – centralizes all navigation logic for the application.
 * 
 * Author: Samano CASTRE
 * Date: January 19, 2026
 * 
 * Responsibilities:
 * - Provides generic navigation methods for entities defined in DOMAIN_LAYOUT_CONFIGS.
 * - Exposes utility navigation functions for common routes (login, dashboard, error pages, external links).
 * - Reduces repetition by handling entity-based routes dynamically.
 * 
 * Behavior:
 * - Wraps React Router's `useNavigate` hook for internal routing.
 * - Uses DOMAIN_LAYOUT_CONFIGS to resolve the base path for any entity.
 * - Handles special cases such as the 'Document' entity which supports a formType parameter.
 * 
 * Returns:
 * - goTo(page: string) – navigate to any arbitrary path.
 * - goToEnigma() – navigate to root ('/').
 * - goToLogin() – navigate to the login page.
 * - goToLogOut() – navigate to the logout page.
 * - goToDashboard() – navigate to the dashboard.
 * - goToIndex() – navigate to root ('/').
 * - goToHome() – navigate to home page.
 * - goToErrorPage(code?: number) – navigate to an error page with a specific code (default 500).
 * - goToGoogle() – navigate to Google's website in the current window.
 * - goToLayout(entity: DomainType) – navigate to the main layout page of the specified entity.
 * - goToDetail(entity: DomainType, id: number) – navigate to the detail page of the entity with the given ID.
 * - goToForm(entity: DomainType, id?: number, formType?: string) – navigate to the form page of the entity, optionally with ID or formType for special cases.
 */
export const useAppNavigation = () => {
    const navigate = useNavigate();

    return useMemo(() => ({

        /** 
         * Navigate to an arbitrary path.
         * @param page – string path to navigate to.
         */
        goTo: (page: string) => navigate(page),


        /** Navigate to the root '/' (default enigma page). */
        goToEnigma: () => navigate('/'),


        /** Navigate to login page. */
        goToLogin: () => navigate('/login'),


        /** Navigate to logout page. */
        goToLogOut: () => navigate('/logout'),


        /** Navigate to dashboard page. */
        goToDashboard: () => navigate('/dashboard'),


        /** Navigate to index page (root '/'). */
        goToIndex: () => navigate('/'),


        /** Navigate to home page. */
        goToHome: () => navigate('/home'),


        /** Navigate to error page with optional code (default 500). */
        goToErrorPage: (code = 500, reason = "") => navigate(`/error/${code}/?reason=${reason}`),


        /** Navigate to external Google page. */
        goToGoogle: () => window.location.href = "https://www.google.com",


        /**
         * Navigate to the layout page of a specific entity.
         * @param entity – DomainType (must exist in DOMAIN_LAYOUT_CONFIGS).
         */

        goToLayout: (entity: DomainType) => {
            const basePath = DOMAIN_CONFIGS[entity].domainRoute;
            if (!basePath) throw new Error(`Unknown entity: ${entity}`);
            navigate(basePath);
        },

        goToSelector: (entity: DomainType) => {
            const basePath = DOMAIN_CONFIGS[entity].domainRoute;
            if (!basePath) throw new Error(`Unknown entity: ${entity}`);
            navigate(`${basePath}/selector`);
        },


        /**
         * Navigate to the detail page of a specific entity by ID.
         * @param entity – DomainType (must exist in DOMAIN_LAYOUT_CONFIGS).
         * @param id – numeric ID of the entity.
         */
        goToDetail: (entity: DomainType, id: number) => {
            const basePath = DOMAIN_CONFIGS[entity].domainRoute;
            if (!basePath) throw new Error(`Unknown entity: ${entity}`);
            navigate(`${basePath}/${id}`);
        },


        /**
         * Navigate to the form page of a specific entity.
         * Special case: 'Document' entity can use formType for new forms.
         * @param entity – DomainType (must exist in DOMAIN_LAYOUT_CONFIGS).
         * @param id – optional numeric ID for editing an existing entity.
         * @param formType – optional string for special forms (used for 'Document').
         */
        goToForm: (entity: DomainType, id?: number, formType?: string) => {
            const basePath = DOMAIN_CONFIGS[entity].domainRoute;
            if (!basePath) throw new Error(`Unknown entity: ${entity}`);

            if (entity === 'Document' && !id) {

                // Special case for Document with formType
                navigate(`${basePath}/selector/${formType ?? ''}`);
            } else if (id) {
                navigate(`${basePath}/${id}/edit`);
            } else {
                navigate(`${basePath}/new`);
            }
        },

    }), [navigate]);
};
